import { Fragment } from "react";

import { PalButton, PalSelect, PalSwitch, PalTextField, PalTooltip, PalTypography } from "@palamar/fe-library";
import { withCardon } from "cardon";
import chroma from "chroma-js";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import styled from "@emotion/styled";
import { Help } from "@mui/icons-material";
import { Box, Fade, Grid } from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";

import PRModal from "~components/Generic/PRModal";
import { popupSettingsMapper } from "~constants";
import StyledHelper from "~helpers/StyledHelper";
import ThemeMapper from "~helpers/ThemeMapper";
import Utils from "~helpers/Utils";

export const headerHeight = 80;
export const stepbarHeight = 60;
export const actionBarHeight = 40 + 16 * 2;
export const borderColor = "#e0e0e0";

export const IFRAME_PREVIEW_ID = "popup-preview";

export const asyncReader = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      let data = e.target.result;
      resolve(data);
    };
    reader.onerror = (e) => {
      reject(e);
    };
    reader.readAsText(file);
  });
};
export const StyledDiv = styled.div`
  background-color: #fff;
  position: relative;
  width: 100%;
  height: 100%;
`;

export const LogoWrapper = styled.div`
  display: flex;
  margin-top: 0px;
  margin-left: 0px;
  svg {
    width: 160px;
  }
`;

export const HeaderBarGrid = styled(Grid)`
  height: ${headerHeight}px;

  padding: 0 ${(props) => props.theme.spacing(3)};
  border-bottom: 1px solid ${borderColor};
`;

export const PanelLeft = styled(Box)`
  width: 100%;
  height: calc(100% - ${actionBarHeight}px);
  position: relative;
  overflow: visible;
  max-width: ${(props) => props.theme.breakpoints.values.md}px; //600px
  margin-left: auto;
  margin-right: auto;
  display: flex;
  align-items: center;
`;

export const panelRightWidthSmall = 400;
export const panelRightWidthLarge = 500;
export const PanelRight = styled(Box, StyledHelper.sfw("fullHeight"))`
  background-color: #f5f5f5;
  height: ${(props) => (props.fullHeight ? "100%" : "calc(100vh - " + headerHeight + "px)")};
  border-left: 1px solid ${borderColor};
  position: relative;
  animation: animateBg 21s linear infinite;
  background-image: linear-gradient(
    90deg,
    #cf5c5c44,
    #c19b4a44,
    #def2a044,
    #c6ee4a44,
    #42eca644,
    #64b3d944,
    #208ea244,
    #498ada44,
    #5b73df44,
    #897ed344,
    #cf5c5c44,
    #c19b4a44
  );
  background-size: 1100% 100%;

  @keyframes animateBg {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: 100% 0%;
    }
  }
`;

export const RightImgWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
`;

export const RightStepInfo = styled.div`
  position: absolute;
  bottom: ${40}px;
  left: 50%;
  transform: translateX(-50%);
  padding: ${(props) => props.theme.spacing(2)};
  background-color: rgba(255, 255, 255);
  border-radius: 4px;
  border: 1px solid ${borderColor};
  width: calc(100% - 40px * 2);
`;

export const StepContainer = styled.div`
  padding: ${(props) => props.theme.spacing(3)};
  padding-top: 0;
`;

export const StepFooterContainerGrid = styled(Grid)`
  border-top: 1px solid ${borderColor};
  height: ${actionBarHeight}px;
  padding: 0 ${(props) => props.theme.spacing(2)};
`;

export const NavigateButton = styled(PalButton)`
  height: 40px;
  font-size: ${(props) => props.theme.typography.body1.fontSize};
  line-height: 1.5;
`;

export const LightTooltip = styled(({ className, ...props }) => (
  <PalTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#444444",
    maxWidth: 500,
    fontSize: theme.typography.pxToRem(14),
  },
}));

export const RequiredComp = () => {
  return (
    <Box color="error.main" component="span">
      &nbsp;*
    </Box>
  );
};
export const setPreviewSettings = async (state, { botName } = {}) => {
  const iframeDom = document.getElementById(IFRAME_PREVIEW_ID);
  const iframeWindow = iframeDom?.contentWindow;
  const frameDocument = iframeWindow?.document;
  if (!frameDocument) return;
  // const framePopup = iframeWindow?.popup;
  const framePopup = frameDocument.getElementById("_palamar_chat_iframe")?.contentWindow?.popup;
  await framePopup?.enablePreviewMessages({ botName: botName || state.popup_title });
  // const settingState = selectPopupSettingsState(store.getState());
  const welcomeFormBodyForPopup = {
    // form_items: settingState.welcome_form?.form_items,
    // popup_button_icon_path: settingState.popup_button_icon_path,
    // popup_button_type: settingState.popup_button_type,
    // popup_header_icon_path: settingState.popup_header_icon_path,
    // popup_header_icon_type: settingState.popup_header_icon_type,
    // popup_bot_icon_type: settingState.popup_bot_icon_type,
    // popup_bot_icon_path: settingState.popup_bot_icon_path,
    // popup_primary_color: settingState.popup_primary_color,
    // popup_title: settingState.popup_title,
    // popup_welcome_text: settingState.popup_welcome_text,
    // start_anonymous_chat: settingState.welcome_form?.optional,
    // welcome_msg: settingState.welcome_msg,
    // popup_style_info: settingState.popup_style_info,
    // chatbot_info: settingState.chatbot_info,
    // system_chatbot_info: settingState.system_chatbot_info,
    ...state,
  };

  const primaryColor = "#50C8F4";
  const light = chroma(primaryColor).brighten(1.5).hex();
  // const dark = chroma(primaryColor).darken(0.7).hex();
  const titleColor = Utils.determineTextColor(primaryColor);
  const contrastColor = Utils.determineTextColor(light);
  const advancedStyle = ThemeMapper.initializeStyle({
    primary_color: primaryColor,
    header_color: titleColor,
    header_background_color: primaryColor,
    chat_balloon_customer_text_color: contrastColor,
    chat_balloon_customer_background_color: primaryColor,
    chat_balloon_customer_border: `1px solid ${light}`,
    header_icon_color: titleColor,
  });
  //delete specific style bot and agent configuration. these options will be filled using chat_ballon_left_... variants.
  delete advancedStyle.chat_balloon_bot_text_color;
  delete advancedStyle.chat_balloon_bot_background_color;
  delete advancedStyle.chat_balloon_bot_border;

  delete advancedStyle.chat_balloon_agent_text_color;
  delete advancedStyle.chat_balloon_agent_background_color;
  delete advancedStyle.chat_balloon_agent_border;

  welcomeFormBodyForPopup.chatbot_info ??= {};
  welcomeFormBodyForPopup.chatbot_info = {
    ...welcomeFormBodyForPopup.chatbot_info,
    enable_advanced_style: true,
    style: advancedStyle,
  };

  if (welcomeFormBodyForPopup.popup_button_icon_path instanceof File) {
    const file = welcomeFormBodyForPopup.popup_button_icon_path;
    const result = await asyncReader(file);
    welcomeFormBodyForPopup.popup_button_icon_path = result;
  }

  const mappedData = popupSettingsMapper(welcomeFormBodyForPopup);
  framePopup.IFrameHelper.setPropertyToRoot([
    ["--webchat-icon-margin-x", `${mappedData.popup_style_info.popup_margin_x}px`],
    ["--webchat-icon-margin-y", `${mappedData.popup_style_info.popup_margin_y}px`],
    ["--webchat-icon-size", `${mappedData.popup_style_info.popup_icon_size}px`],
    ["--webchat-height", `${mappedData.popup_style_info.chat_height}px`],
    ["--webchat-width", `${mappedData.popup_style_info.chat_width}px`],
  ]);
  framePopup.setWelcomeOptions(mappedData);
  // framePopup.setViewModeDelayed(settingState.welcome_form?.optional ? "chat" : "welcome");
  framePopup.setViewModeDelayed("chat");
  // framePopup.setWelcomeMsgShowedStatus(false);
  framePopup.setPopupSizeModePosition(mappedData?.popup_style_info?.popup_position);
};
export const showPopupPreviewBalloon = (state) => {
  const iframeDom = document.getElementById(IFRAME_PREVIEW_ID);
  const iframeWindow = iframeDom?.contentWindow;
  const frameDocument = iframeWindow?.document;
  if (!frameDocument) return;
  const framePopup = frameDocument.getElementById("_palamar_chat_iframe")?.contentWindow?.popup;

  framePopup.setWelcomeMsgShowedStatus(state);
};
export const HelpIconWrapper = ({ title, children }) => {
  return (
    <Box alignItems={"center"} display="flex" gap={0.5}>
      {children}
      <LightTooltip
        enterDelay={0}
        leaveDelay={0}
        placement="top"
        title={title}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 0 }}
      >
        <Help fontSize="small" />
      </LightTooltip>
    </Box>
  );
};

export const IntegrationDetailModal = withCardon(
  function IntegrationDetailModalContent({ get, integration, labelDefinition }) {
    const { t } = useTranslation();
    const excludeKeyList = ["id", "access_token", "secret", "project"];
    const entries = Object.entries(integration);
    const filteredEntries = entries.filter(([key]) => !excludeKeyList.includes(key));
    //order entries. first one must be "integration_type"
    const orderedEntries = filteredEntries.sort(([key1], [key2]) => {
      if (key1 === "integration_type") return -1;
      if (key2 === "integration_type") return 1;
      return 0;
    });
    const getLabel = (key) => {
      if (labelDefinition) {
        const label = labelDefinition[key];
        if (label) return label;
      }
      switch (key) {
        case "integration_type":
          return t("dashboard.onboarding.integration.integrationDetail.integrationType");
        case "name":
          return t("dashboard.onboarding.integration.integrationDetail.name");
        case "app_id":
          return t("dashboard.onboarding.integration.integrationDetail.appId");
        case "number":
          return t("dashboard.onboarding.integration.integrationDetail.number");
        default:
          return key;
      }
    };

    return (
      <PRModal size="lg" title={"Integration Details"} onClose={get(false)}>
        <Grid container spacing={1}>
          {orderedEntries.map(([key, value]) => {
            if (value === null || value === undefined) return null;
            return (
              <Fragment key={key}>
                <Grid key={key} item md={5} xs={12}>
                  <PalTypography fontWeight={600} variant="body1">
                    {getLabel(key)}
                  </PalTypography>
                </Grid>
                <Grid key={key} item md={7} xs={12}>
                  <PalTypography variant="body2">
                    {value === "" ? (
                      <Box color="secondary.main" component={"span"}>
                        N/A
                      </Box>
                    ) : (
                      value
                    )}
                  </PalTypography>
                </Grid>
              </Fragment>
            );
          })}
        </Grid>
      </PRModal>
    );
  },
  { destroyOnHide: true }
);

export const ManualIntegrationModal = withCardon(
  function ManualIntegrationModalContent({ integrationName, get, inputFields, state, showDelete }) {
    const { t } = useTranslation();
    const formik = useFormik({
      initialValues: inputFields.reduce((acc, item) => {
        acc[item.key] = state?.[item.key] || "";
        return acc;
      }, {}),
      validationSchema: Yup.object().shape(
        inputFields.reduce((acc, item) => {
          if (item.required) {
            if (item.type === "switch") {
              acc[item.key] = Yup.boolean().required("Required").nullable();
            } else {
              acc[item.key] = Yup.string().required("Required").nullable();
            }
          }
          return acc;
        }, {})
      ),
      onSubmit: async (values) => {
        get(values)();
      },
    });
    const toCapitalCase = (str) => {
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const handleOnDelete = async () => {
      //set all values as "";
      const emptyValues = inputFields.reduce((acc, item) => {
        acc[item.key] = "";
        return acc;
      }, {});
      formik.setValues(emptyValues);
      get(emptyValues)();
    };
    const modalTitle = t("dashboard.onboarding.integration.manualIntegration").format(toCapitalCase(integrationName));
    return (
      <PRModal
        size="xl"
        title={modalTitle}
        onClick={formik.handleSubmit}
        onClose={get(false)}
        {...(showDelete && { onDelete: handleOnDelete })}
      >
        <Grid container spacing={2}>
          {inputFields.map((item, index) => {
            return (
              <Grid
                key={index}
                item
                {...(!item?.fullWidth
                  ? {
                      md: 6,
                      sm: 12,
                      xs: 12,
                    }
                  : {
                      xs: 12,
                    })}
              >
                {(() => {
                  if (item?.type === "switch") {
                    const handleCheck = (e) => {
                      formik.setFieldValue(item.key, e.target.checked);
                    };
                    return (
                      <Box alignItems="center" display="flex" gap={1}>
                        <PalSwitch
                          fullWidth
                          checked={formik.values[item.key]}
                          invalid={formik.errors[item.key]}
                          name={item.key}
                          required={item.required}
                          onChange={handleCheck}
                        />
                        <PalTypography variant="body2">{t(item.label)}</PalTypography>
                      </Box>
                    );
                  } else {
                    return (
                      <PalTextField
                        fullWidth
                        invalid={formik.touched[item.key] && formik.errors[item.key]}
                        label={t(item.label)}
                        name={item.key}
                        required={item.required}
                        value={formik.values[item.key]}
                        onChange={formik.handleChange}
                      />
                    );
                  }
                })()}
              </Grid>
            );
          })}
        </Grid>
      </PRModal>
    );
  },
  { destroyOnHide: true }
);

export const FacebookMessengerPageSelectModal = withCardon(
  function ManualIntegrationModalContent({ pages, get }) {
    const { t } = useTranslation();
    const formik = useFormik({
      initialValues: {
        page: null,
        pageId: null,
      },
      validationSchema: Yup.object().shape({
        pageId: Yup.string().required("Required").nullable(),
      }),
      onSubmit: async (values) => {
        get(values?.page)();
      },
    });
    const modalTitle = t("dashboard.onboarding.integration.facebookPageSelect.title");

    const handleChangeSelect = (e, item) => {
      formik.setFieldValue("page", item);
      formik.setFieldValue("pageId", item.id);
    };
    return (
      <PRModal size="md" title={modalTitle} onClick={formik.handleSubmit} onClose={get(false)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PalSelect
              fullWidth
              required
              invalid={formik.errors.pageId}
              label={t("dashboard.onboarding.integration.facebookPageSelect.placeholder")}
              labelSelector="name"
              options={pages}
              value={formik.values.page}
              valueSelector="id"
              onChange={handleChangeSelect}
            />
          </Grid>
        </Grid>
      </PRModal>
    );
  },
  { destroyOnHide: true }
);
